<div class="page p-8">
  <h1 class="mb-6 shrink-0 text-3xl font-medium">{{ 'availability.title' | translate }}</h1>

  <mat-card class="mb-6 shrink-0">
    <mat-card-content class="!pt-5">
      <div class="flex flex-wrap items-center gap-4">
        <!-- Multi-select with search, split in two: this field only ever *adds* a lake, and what is
             selected lives in the chip row below. Material's chip-grid-inside-the-field pattern would put
             them here instead, but the field then grows with every pick and drags this whole toolbar
             around with it. Type straight into it; ~110 lakes arrive as one code list, so the filtering is
             local. Only known lakes can be picked, so free text never becomes a selection. -->
        <mat-form-field class="min-w-80" subscriptSizing="dynamic">
          <mat-label>{{ 'availability.add-water' | translate }}</mat-label>
          <input
            matInput
            #waterInput
            [matAutocomplete]="waterAuto"
            (input)="onWaterSearch($event)"
            (blur)="onWaterBlur(waterInput)"
            placeholder="{{ 'availability.water-search' | translate }}" />
          <mat-autocomplete #waterAuto="matAutocomplete" (optionSelected)="addWater($event.option.value, waterInput)">
            @for (water of filteredWaters(); track water.id) {
              <mat-option [value]="water.id">{{ water.name }}</mat-option>
            }
          </mat-autocomplete>
          <mat-icon matIconSuffix>search</mat-icon>
        </mat-form-field>

        <div class="flex items-center gap-1">
          <button
            mat-icon-button
            type="button"
            (click)="previousMonth()"
            [attr.aria-label]="'availability.previous-month' | translate"
            [matTooltip]="'availability.previous-month' | translate">
            <mat-icon>chevron_left</mat-icon>
          </button>

          <!-- The month itself is the control: it shows which month is on screen and opens the picker.
               A month/year picker needs a registered datepicker input to open from, but no date is ever
               typed into it, so it is present for the datepicker and hidden from everything else. -->
          <input
            class="sr-only"
            readonly
            tabindex="-1"
            aria-hidden="true"
            [matDatepicker]="monthPicker"
            [value]="anchor()" />
          <button
            mat-stroked-button
            type="button"
            class="min-w-44"
            (click)="monthPicker.open()"
            [attr.aria-label]="'availability.change-month' | translate">
            {{ windowStart() | date: 'MMMM y' }}
            <mat-icon iconPositionEnd>arrow_drop_down</mat-icon>
          </button>
          <!-- Opens on the year grid and commits on the month, so the day grid is never reached. -->
          <mat-datepicker #monthPicker startView="multi-year" (monthSelected)="onMonthSelected($event, monthPicker)" />

          <button
            mat-icon-button
            type="button"
            (click)="nextMonth()"
            [attr.aria-label]="'availability.next-month' | translate"
            [matTooltip]="'availability.next-month' | translate">
            <mat-icon>chevron_right</mat-icon>
          </button>
        </div>

        <button mat-stroked-button type="button" (click)="thisMonth()">{{ 'availability.this-month' | translate }}</button>

        @if (isLoading()) {
          <mat-spinner diameter="24" />
        }
      </div>

      <!-- The selection, on its own line so it can wrap as far as it likes without moving the toolbar. -->
      @if (selectedWaters().length > 0) {
        <div class="mt-4 flex flex-wrap items-center gap-2">
          <mat-chip-set [attr.aria-label]="'availability.waters' | translate">
            @for (water of selectedWaters(); track water.id) {
              <mat-chip (removed)="removeWater(water.id)">
                {{ water.name }}
                <button matChipRemove [attr.aria-label]="'availability.remove-water' | translate: { name: water.name }">
                  <mat-icon>cancel</mat-icon>
                </button>
              </mat-chip>
            }
          </mat-chip-set>

          @if (selectedWaters().length > 1) {
            <button mat-button type="button" (click)="clearWaters()">
              {{ 'availability.clear-waters' | translate }}
            </button>
          }
        </div>
      }
    </mat-card-content>
  </mat-card>

  @if (selectedWaterIds().length === 0) {
    <p class="text-gray-500 dark:text-gray-400">{{ 'availability.pick-water' | translate }}</p>
  } @else if (!hasAnyLoad()) {
    <div class="flex justify-center py-12">
      <mat-spinner diameter="40" />
    </div>
  } @else {
    <!-- No card around the chart: it is the widest thing on the page and wants every pixel, and a card
         header would only repeat the month the toolbar's own button already names. The scroll box carries
         the panel's border and rounding itself. -->
    <!-- The chart's own scroll viewport, which is what the sticky axes stick to: the item column to its
         left edge, the day axis to its top, and each lake's header just under that. -->
    <div class="tape-scroll">
      <table class="tape">
        <thead>
          <tr>
            <th class="label-col corner">{{ 'availability.column.item' | translate }}</th>
            <!-- The columns are only a ruler: a weekday initial and the day number, nothing else. The
                 initial is what makes the changeover rhythm legible — on a Saturday-week lake every bar
                 starts under an S. -->
            @for (column of dayColumns(); track column.date) {
              <th class="day-col" [class.is-today]="column.isToday">
                <div class="day-weekday">{{ column.value | date: 'EEEEE' }}</div>
                <div>{{ column.dayOfMonth }}</div>
              </th>
            }
          </tr>
        </thead>

        <!-- One tbody per lake. That is what scopes the lake's sticky header to its own band: a
             sticky cell is held inside its row group, so each header sticks under the day axis only
             while its own lake is on screen and is pushed off by the next one. -->
        @for (band of bands(); track band.waterId) {
        <tbody class="lake">
          @if (band.failed) {
            <!-- Only this lake failed. Its header still names it and offers the retry, so the lakes
                 that did load are untouched — the point of reading them independently. -->
            <tr class="season-row lake-header">
              <th class="label-col lake-name">
                <ng-container [ngTemplateOutlet]="lakeName" [ngTemplateOutletContext]="{ band: band }" />
              </th>
              <td class="track lake-error" [attr.colspan]="dayCount()">
                <span>{{ 'availability.lake-load-failed' | translate }}</span>
                <button mat-button type="button" (click)="retryLake(band.waterId)">
                  {{ 'availability.retry' | translate }}
                </button>
              </td>
            </tr>
          } @else {
          <!-- The season lane doubles as the lake's header: its label cell carries the lake name, and
               the whole row sticks — so scrolling a lake's swims keeps both its name and which season
               the days are in. Overlapping periods stack rather than being resolved into one value per
               day, because a closed stretch inside a high season is two facts, not a conflict. -->
          @if (band.periodLanes.length === 0) {
            <tr class="season-row lake-header">
              <th class="label-col lake-name">
                <ng-container [ngTemplateOutlet]="lakeName" [ngTemplateOutletContext]="{ band: band }" />
              </th>
              <td class="track text-sm text-gray-500 dark:text-gray-400" [attr.colspan]="dayCount()">
                {{ 'availability.no-periods' | translate }}
              </td>
            </tr>
          } @else {
            @for (lane of band.periodLanes; track $index; let first = $first) {
              <tr class="season-row lake-header">
                @if (first) {
                  <th class="label-col lake-name" [attr.rowspan]="band.periodLanes.length">
                    <ng-container [ngTemplateOutlet]="lakeName" [ngTemplateOutletContext]="{ band: band }" />
                  </th>
                }
                @for (cell of lane; track $index) {
                  @if (cell.bar; as bar) {
                    <td class="track" [attr.colspan]="cell.span">
                      <div
                        class="bar season-bar"
                        [class.is-closed]="bar.period.isClosed"
                        [class.is-exception]="bar.period.exceptionYear !== null"
                        [attr.data-season]="bar.period.periodTypeId"
                        [matTooltip]="periodTooltip(bar.period)">
                        <span class="bar-label">{{ bar.period.periodTypeName }}</span>
                      </div>
                    </td>
                  } @else {
                    <td class="track" [attr.colspan]="cell.span"></td>
                  }
                }
              </tr>
            }
          }

          <!-- Bookable items: whole lake, raffle, swims, accommodations — ordered by the API. -->
          @if (band.itemRows.length === 0) {
            <tr class="item-row">
              <!-- No label: the message says what the row is, and repeating the column header here reads
                   as a stray second header. -->
              <th class="label-col"></th>
              <td class="track text-sm text-gray-500 dark:text-gray-400" [attr.colspan]="dayCount()">
                {{ 'availability.no-items' | translate }}
              </td>
            </tr>
          } @else {
            <!-- Raffle rows go through exactly the same markup, just deeper: rod places are a shared
                 pool, so the row is `capacity` sub-lanes and a booking fills as many as it holds rods.
                 Filled height is occupancy, the empty lanes above are what is still free. Sharing the
                 markup is also what keeps the columns aligned — every row is per-day cells on the one
                 grid, so the browser does the lining up. -->
            @for (entry of band.itemRows; track entry.row.kind + '-' + entry.row.itemId) {
              @for (lane of entry.lanes; track $index; let first = $first; let last = $last; let laneIndex = $index) {
                <!-- Sub-lanes are one item's band, so the rule between them is suppressed: it would cut a
                     hatched stretch, a run of free track or a multi-rod block into slices. -->
                <tr
                  class="item-row"
                  [class.raffle-row]="entry.raffle !== null"
                  [class.not-offered]="!entry.row.isOffered"
                  [class.lane-continues]="!last"
                  [class.capacity-boundary]="laneIndex === entry.raffle?.boundaryLaneFromTop">
                  @if (first) {
                    <th class="label-col item-head" [attr.rowspan]="entry.lanes.length">
                      <ng-container [ngTemplateOutlet]="itemLabel" [ngTemplateOutletContext]="{ row: entry.row }" />
                      @if (entry.raffle; as raffle) {
                        <div class="text-xs font-normal text-gray-500 dark:text-gray-400">
                          {{ 'availability.capacity' | translate: { capacity: raffle.capacity } }}
                        </div>
                      }
                    </th>
                  }

                  @for (cell of lane; track $index) {
                    @if (cell.bar; as bar) {
                      <!-- A booking inside a closed period keeps the wash: the bar paints over its cell, so
                           the two facts stack rather than one hiding the other. -->
                      <td
                        class="track"
                        [class.is-blocked]="cell.blockedBy !== null"
                        [class.blocked-closed]="cell.blockedBy === 'closed'"
                        [attr.colspan]="cell.span">
                        <a
                          class="bar claim-bar"
                          [class.is-conflict]="bar.isConflict"
                          [class.is-stale]="bar.isStale"
                          [class.continues-before]="bar.continuesBefore"
                          [class.continues-after]="bar.continuesAfter"
                          [class.block-top]="bar.isBlockTop"
                          [class.block-bottom]="bar.isBlockBottom"
                          cdkOverlayOrigin
                          #claimOrigin="cdkOverlayOrigin"
                          [routerLink]="['/reservations/edit', bar.claim.reservationId]"
                          [queryParams]="reservationLinkParams()"
                          (mouseenter)="openSummary(bar.claim)"
                          (mouseleave)="scheduleClose()"
                          (focus)="openSummary(bar.claim)"
                          (blur)="scheduleClose()">
                          <!-- A multi-rod block is one booking drawn across several lanes, so only its top
                               lane is labelled; the rest are the same bar continuing downwards. -->
                          @if (bar.isBlockTop) {
                            <span class="bar-label">
                              {{ bar.label }}
                              @if (bar.rods) {
                                <span class="bar-rods">{{ 'availability.rods' | translate: { count: bar.rods } }}</span>
                              }
                            </span>
                          }
                        </a>

                        <!-- Attached to the labelled lane only: one panel per booking, not one per rod. -->
                        @if (bar.isBlockTop) {
                          <ng-container
                            [ngTemplateOutlet]="summaryPanel"
                            [ngTemplateOutletContext]="{ claim: bar.claim, origin: claimOrigin }" />
                        }
                      </td>
                    } @else if (cell.blockedBy; as reason) {
                      <!-- Free of its own claims but not sellable: a closed season, or the whole-lake and
                           unit rows taking each other out. Crossed off rather than filled, so it never
                           reads as somebody's booking. -->
                      <td
                        class="track is-blocked"
                        [class.blocked-closed]="reason === 'closed'"
                        [attr.colspan]="cell.span"
                        [matTooltip]="blockedTooltip(reason)"></td>
                    } @else {
                      <td class="track" [attr.colspan]="cell.span"></td>
                    }
                  }
                </tr>
              }
            }
          }
          }
        </tbody>
        }
      </table>
    </div>

    <div class="mt-4 flex shrink-0 flex-wrap gap-x-6 gap-y-2 text-xs text-gray-500 dark:text-gray-400">
      <span class="flex items-center gap-1.5"><span class="legend-swatch legend-claimed"></span>{{ 'availability.legend.claimed' | translate }}</span>
      <span class="flex items-center gap-1.5"><span class="legend-swatch legend-conflict"></span>{{ 'availability.legend.conflict' | translate }}</span>
      <span class="flex items-center gap-1.5"><span class="legend-swatch legend-blocked"></span>{{ 'availability.legend.blocked' | translate }}</span>
      <span class="flex items-center gap-1.5"><span class="legend-swatch legend-closed"></span>{{ 'availability.legend.closed' | translate }}</span>
      <span class="flex items-center gap-1.5"><mat-icon class="legend-icon">sync_problem</mat-icon>{{ 'availability.legend.stale' | translate }}</span>
      <span>{{ 'availability.legend.read-only' | translate }}</span>
    </div>
  }
</div>

<!-- A lake's header label: its name, and the way to its season periods. -->
<ng-template #lakeName let-band="band">
  <div class="flex items-center gap-2">
    <span class="truncate">{{ band.name }}</span>
  </div>
  <a
    class="lake-edit"
    [routerLink]="periodsEditLink(band.waterId)"
    [matTooltip]="'availability.edit-periods' | translate">
    {{ 'availability.edit-periods-short' | translate }}
  </a>
</ng-template>

<!-- An item's label cell, shared by the raffle strip and the named-unit rows. -->
<ng-template #itemLabel let-row="row">
  <div class="flex items-center gap-2">
    <span>{{ row.label }}</span>
    @if (!row.isOffered) {
      <!-- Claims on an item the lake no longer offers take part in no availability read, so this is the
           only place they are visible. -->
      <mat-icon class="warn-icon" [matTooltip]="notOfferedLabel(row)">warning</mat-icon>
    }
  </div>
</ng-template>

<!-- The hover panel. One definition, attached to whichever bar opened it. -->
<ng-template #summaryPanel let-claim="claim" let-origin="origin">
  <ng-template
    cdkConnectedOverlay
    [cdkConnectedOverlayOrigin]="origin"
    [cdkConnectedOverlayOpen]="isSummaryOpen(claim)"
    [cdkConnectedOverlayPositions]="summaryPositions"
    (detach)="scheduleClose()">
    <!-- Hoverable on purpose: it carries an email and a phone number that exist to be copied. -->
    <div class="reservation-summary" (mouseenter)="cancelClose()" (mouseleave)="scheduleClose()">
      @if (summary(claim); as detail) {
        <div class="flex items-baseline justify-between gap-3">
          <span class="font-medium">{{ detail.reservation.reservationNumber ?? '#' + claim.reservationId }}</span>
          @if (detail.reservation.statusName) {
            <span class="text-xs text-gray-500 dark:text-gray-400">{{ detail.reservation.statusName }}</span>
          }
        </div>

        @if (detail.reservation.isStale) {
          <div class="summary-stale">
            <mat-icon class="legend-icon">sync_problem</mat-icon>
            <span>{{ 'availability.summary.stale' | translate }}</span>
          </div>
        }

        <div class="summary-customer">
          <div class="font-medium">
            {{ detail.reservation.customerName ?? ('availability.summary.no-customer' | translate) }}
          </div>
          <!-- Contact details sit on one wrapping line rather than one each: they are short, and stacking
               them made the panel tall and narrow for no gain. -->
          @if (detail.reservation.customerEmail || detail.reservation.customerPhone) {
            <div class="summary-contacts">
              @if (detail.reservation.customerEmail) {
                <a class="summary-contact" [href]="'mailto:' + detail.reservation.customerEmail">
                  {{ detail.reservation.customerEmail }}
                </a>
              }
              @if (detail.reservation.customerPhone) {
                <a class="summary-contact" [href]="'tel:' + detail.reservation.customerPhone">
                  {{ detail.reservation.customerPhone }}
                </a>
              }
            </div>
          }
        </div>

        @if (detail.stay; as stay) {
          <div class="summary-stay">
            <div class="font-medium">{{ stay.itemLabel }}</div>
            <div>
              {{ stay.arrival | date: 'EEE d MMM' }} &rarr; {{ stay.departure | date: 'EEE d MMM y' }}
              &middot; {{ 'availability.summary.nights' | translate: { count: stay.nights } }}
              &middot; {{ 'availability.summary.anglers' | translate: { count: stay.anglers } }}
              @if (stay.nonAnglers > 0) {
                &middot; {{ 'availability.summary.non-anglers' | translate: { count: stay.nonAnglers } }}
              }
            </div>
          </div>
        }

        <!-- The reservation's other stays, including any outside the window — the chart cannot show those
             at all. -->
        @if (detail.otherStays.length > 0) {
          <div class="summary-others">
            <div class="summary-others-heading">
              {{ 'availability.summary.other-lines' | translate: { count: detail.otherStays.length } }}
            </div>
            @for (other of detail.otherStays; track other.itemKind + '-' + other.itemId + '-' + other.arrival) {
              <div class="summary-other">
                <span class="truncate">{{ other.itemLabel }}</span>
                <span class="whitespace-nowrap text-gray-500 dark:text-gray-400">
                  {{ other.arrival | date: 'd MMM' }} &rarr; {{ other.departure | date: 'd MMM' }}
                </span>
              </div>
            }
          </div>
        }
      } @else {
        <!-- No summary means the reservation row itself has gone. -->
        <div class="summary-stale">
          <mat-icon class="legend-icon">sync_problem</mat-icon>
          <span>{{ 'availability.summary.missing' | translate: { id: claim.reservationId } }}</span>
        </div>
      }
    </div>
  </ng-template>
</ng-template>
